import cn from 'classnames';
import { FC, ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';

import { useMedia } from '@/shared/hooks';
import { getSupportEmail } from '@/shared/lib';
import { AuthCard } from '@/shared/ui';

import { useMFAEnrollment } from '../../hooks';
import { MFAEnrollmentActions } from '../MFAEnrollmentActions/MFAEnrollmentActions';
import { MFASetupPanel } from '../MFASetupPanel/MFASetupPanel';
import { RecoveryCodesPanel } from '../RecoveryCodesPanel/RecoveryCodesPanel';

import styles from './MFAEnrollmentCard.module.scss';

type MFAEnrollmentCardProps = {
  title: ReactNode;
  cancelLabel: ReactNode;
  className?: string;
  eyebrow?: ReactNode;
  /** Registration and forced enrolment offer support as the way out; the organization switch does not. */
  withSupportHint?: boolean;
  /** Set when the card is portalled over a page rather than being the page. */
  fixed?: boolean;
  onCancel: () => void;
  onCompleted: () => void;
  onSignInRequired: () => void;
};

/**
 * Enrolment on the sign-in surface: the same setup step as the settings modal,
 * followed by the one-time recovery codes, inside the purple auth card.
 */
export const MFAEnrollmentCard: FC<MFAEnrollmentCardProps> = (props) => {
  const {
    title,
    cancelLabel,
    className,
    eyebrow,
    withSupportHint,
    fixed,
    onCancel,
    onCompleted,
    onSignInRequired,
  } = props;

  const { isMobile } = useMedia();

  const {
    secret,
    otpauthURI,
    code,
    errorMessage,
    recoveryCodes,
    isStarting,
    isConfirming,
    isCodeComplete,
    signInRequired,
    setCode,
    confirm,
  } = useMFAEnrollment();

  if (recoveryCodes.length) {
    return (
      <AuthCard
        fixed={fixed}
        className={className}
        dataSection="mfa-enrollment"
      >
        <RecoveryCodesPanel
          codes={recoveryCodes}
          signInRequired={signInRequired}
          onContinue={signInRequired ? onSignInRequired : onCompleted}
        />
      </AuthCard>
    );
  }

  return (
    <AuthCard
      fixed={fixed}
      className={className}
      cardClassName={styles.card}
      dataSection="mfa-enrollment"
    >
      <div className={styles.intro}>
        {eyebrow && <span className={cn(styles.eyebrow, 'p4')}>{eyebrow}</span>}

        <h3 className={isMobile ? 'h5' : 'h3'}>{title}</h3>
      </div>

      <MFASetupPanel
        secret={secret}
        otpauthURI={otpauthURI}
        code={code}
        isLoading={isStarting}
        disabled={isConfirming}
        errorMessage={errorMessage}
        onCodeChange={setCode}
        onCodeComplete={confirm}
      />

      <div className={styles.footer}>
        {withSupportHint && (
          <p className={cn(styles.supportHint, 'p3')}>
            <FormattedMessage
              id="mfa.setup.supportHint"
              defaultMessage="Can’t set this up right now? <link>Contact support</link>"
              values={{
                link: (chunks) => (
                  <a href={`mailto:${getSupportEmail()}`}>{chunks}</a>
                ),
              }}
            />
          </p>
        )}

        <MFAEnrollmentActions
          className={styles.actions}
          cancelLabel={cancelLabel}
          isStarting={isStarting}
          isConfirming={isConfirming}
          isCodeComplete={isCodeComplete}
          onCancel={onCancel}
          onConfirm={() => confirm(code)}
        />
      </div>
    </AuthCard>
  );
};
